import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import GeoJSON from "ol/format/GeoJSON";
import VectorSource from "ol/source/Vector";
import { GeoJSONFeature } from "ol/format/GeoJSON";

import union from "@turf/union";
import difference from "@turf/difference";

import CookieCut from "lib/classes/cookiecut";
import RemnantPair from "lib/classes/remnantpair";
import { 
	cleanMultiPolygon,
	polygonIntersects,
	polygonIntersections
} from "lib/utilities/feature";

/**
	* Cookie cut feature
	*
	* @param {Feature<Geometry>} feature - Cookie cut feature
	* @param {VectorSource} source - Source of the existing polygons
	* 
	* @returns {CookieCut} New features to insert / remove
	*
	* @remarks
	* Subtracts the cookie polygon from any intersecting polygons on given source
*/
export function cookieCut(cookieFeature:Feature<Geometry>, source:VectorSource):CookieCut {
	// GeoJSON format
	const format:GeoJSON = source.get("feature-format");
	
	// Convert the cookie feature to GeoJSON format
	const geoJsonCookieFeature:GeoJSONFeature = format.writeFeatureObject(cookieFeature);
	
	// Get the intersections with the cookieFeature
	const intersections:Feature<Geometry>[] = polygonIntersections(cookieFeature, source);
	
	// Array for new/old features to insert/delete
	const result:CookieCut = new CookieCut({
		newFeatures: [],
		oldFeatures: []
	});
	
	// Perform @turf->difference on all intersecting polygons
	intersections.forEach((feature:Feature<Geometry>) => {
		if(feature.get("collapsed")) return;
		
		// Convert feature to GeoJSON format
		const geoJsonFeature:GeoJSONFeature = format.writeFeatureObject(feature);
		
		// Perform difference and get new GeoJSON feature
		const newFeatureGeoJSON:GeoJSONFeature = difference(geoJsonFeature as any, geoJsonCookieFeature as any);
		
		// Something went wrong
		if(!newFeatureGeoJSON)
			return;
			
		// Convert back to OpenLayers Feature<Geometry>
		let newFeature:Feature<Geometry> = format.readFeature(newFeatureGeoJSON);
		
		// Clean if its multi polygon (removes tiny polygons)
		if(newFeature.getGeometry().getType() == "MultiPolygon")
			newFeature = cleanMultiPolygon(newFeature);
			
		// Now delete the original feature
		result.oldFeatures.push(feature);
		
		// Clone both features so they can be referenced again correctly (mainly the original)
		result.remnantPairs.push(new RemnantPair({
			originFeature: format.readFeature(geoJsonFeature),
			newFeature: format.readFeature(newFeatureGeoJSON),
			cookieCuttedFeature: newFeature // original reference
		}));
		
		// Insert new feature to array for inserting
		result.newFeatures.push(newFeature);
	});
	
	return result;
}
/**
	* Cookie cut feature
	*
	* @param {Feature<Geometry>} feature - Cookie cut feature
	* @param {VectorSource} source - Source of the existing polygons
	* 
	* @returns {Feature<Geometry>} New feature to insert
	*
	* @remarks
	* Subtracts the cookie polygon from any intersecting polygons on given source
*/
export function cookieCutReverse(newFeature:Feature<Geometry>, oldFeature:Feature<Geometry>, format:GeoJSON):Feature<Geometry> {
	// Convert the new feature to GeoJSON format
	const geoJsonNewFeature:GeoJSONFeature = format.writeFeatureObject(newFeature);
	
	// Convert old feature to GeoJSON format
	const geoJsonOldFeature:GeoJSONFeature = format.writeFeatureObject(oldFeature);
	
	// Perform difference and get new GeoJSON feature
	const differenceGeoJSON:GeoJSONFeature = difference(geoJsonOldFeature as any, geoJsonNewFeature as any);
	const unionFeatureGeoJSON:GeoJSONFeature = union(geoJsonOldFeature as any, differenceGeoJSON as any);
	
	// Something went wrong
	if(!unionFeatureGeoJSON)
		return;
		
	// Convert back to OpenLayers Feature<Geometry>
	const feature:Feature<Geometry> = format.readFeature(unionFeatureGeoJSON);
	
	/*
		Clone the properties over (apart from the geometry)
	*/
		const props:any = newFeature.getProperties();
		
		delete props.geometry;
		
		feature.setProperties(props);
	
	return feature;
}